import { Controller } from "@hotwired/stimulus";

export class TimeTypeToggleController extends Controller {
  static targets = [
    "timeTypeSelect",
    "relativeAnchorSelect",
    "laterGroup",
    "recurringGroup",
    "relativeGroup",
    "triggeredGroup",
    "triggerTypeSelect",
    "triggerValueSelect",
    "recipientsFilterSelect",
    "audienceGroup"
  ];

  connect() {
    if (this.hasTimeTypeSelectTarget) {
      this.setVisibility();
    } else if (this.hasTriggerTypeSelectTarget) {
      // Edit form for triggered campaigns: Schedule dropdown not present
      this.toggleTriggerValue();
    }
  }

  setVisibility() {
    const currentValue = this.timeTypeSelectTarget.value;
    const hasType = currentValue !== "";

    if (this.hasAudienceGroupTarget) {
      this.audienceGroupTarget.hidden = !hasType;
    }

    switch (currentValue) {
    case "later":
      this.setLaterVisibility(true);
      this.setEveryNDaysVisibility(false);
      this.setRelativeVisibility(false);
      this.setTriggeredVisibility(false);
      break;
    case "every_n_days_if_pending":
      this.setLaterVisibility(false);
      this.setEveryNDaysVisibility(true);
      this.setRelativeVisibility(false);
      this.setTriggeredVisibility(false);
      break;
    case "relative":
      this.setLaterVisibility(false);
      this.setEveryNDaysVisibility(false);
      this.setRelativeVisibility(true);
      this.setTriggeredVisibility(false);
      break;
    case "triggered":
      this.setLaterVisibility(false);
      this.setEveryNDaysVisibility(false);
      this.setRelativeVisibility(false);
      this.setTriggeredVisibility(true);
      break;
    default:
      this.setLaterVisibility(false);
      this.setEveryNDaysVisibility(false);
      this.setRelativeVisibility(false);
      this.setTriggeredVisibility(false);
    }
  }

  setLaterVisibility(visible) {
    this.toggleGroupVisibility(this.laterGroupTargets, visible);
    this.togglePrimerFields(".time-type-toggle-later", visible);
  }

  setEveryNDaysVisibility(visible) {
    this.toggleGroupVisibility(this.recurringGroupTargets, visible);
    this.togglePrimerFields(".time-type-toggle-every-n-days", visible);
  }

  setRelativeVisibility(visible) {
    this.toggleGroupVisibility(this.relativeGroupTargets, visible);
    this.togglePrimerFields(".time-type-toggle-relative", visible);

    this.updateRelativeCustomDateVisibility(visible);
  }

  setTriggeredVisibility(visible) {
    this.toggleGroupVisibility(this.triggeredGroupTargets, visible);
    this.togglePrimerFields(".time-type-toggle-triggered", visible);

    if (visible) {
      this.toggleTriggerValue();
    } else {
      this.updateRecipientsForTrigger();
    }
  }

  toggleRelativeCustomDate() {
    const isRelative = this.hasTimeTypeSelectTarget && this.timeTypeSelectTarget.value === "relative";
    this.updateRelativeCustomDateVisibility(isRelative);
  }

  updateRelativeCustomDateVisibility(relativeVisible) {
    const anchorSelect = this.hasRelativeAnchorSelectTarget ? this.relativeAnchorSelectTarget : null;
    const showCustom = relativeVisible && anchorSelect && anchorSelect.value === "custom_date";

    const customElements = this.element.querySelectorAll(".time-type-toggle-relative-custom");
    customElements.forEach((el) => {
      this.togglePrimerField(el, !showCustom);
    });
  }

  toggleTriggerValue() {
    if (!this.hasTriggerTypeSelectTarget || !this.hasTriggerValueSelectTarget) return;

    const selectedType = this.triggerTypeSelectTarget.value;
    const selectEl = this.triggerValueSelectTarget;

    // Hide trigger value select until a trigger type is chosen
    const valueFormControl = selectEl.closest(".FormControl");
    if (valueFormControl) {
      valueFormControl.hidden = !selectedType;
    }

    const options = selectEl.querySelectorAll("option[data-trigger-group]");

    options.forEach((option) => {
      const group = option.dataset.triggerGroup;
      if (group === selectedType) {
        option.hidden = false;
        option.disabled = false;
      } else {
        option.hidden = true;
        option.disabled = true;
        option.selected = false;
      }
    });

    // Reset selection if current value doesn't match new type
    const currentOption = selectEl.selectedOptions[0];
    if (currentOption && currentOption.dataset.triggerGroup && currentOption.dataset.triggerGroup !== selectedType) {
      selectEl.value = "";
    }

    this.updateRecipientsForTrigger();
  }

  updateRecipientsForTrigger() {
    if (!this.hasRecipientsFilterSelectTarget) return;

    const selectEl = this.recipientsFilterSelectTarget;

    const isTriggered = this.hasTimeTypeSelectTarget
      ? this.timeTypeSelectTarget.value === "triggered"
      : this.hasTriggerTypeSelectTarget; // edit form for triggered campaigns

    // For triggered campaigns, the trigger defines status targeting.
    // Only show "All" and "Report Based" — hide status-based options.
    const triggeredAllowedValues = ["unfiltered", "report"];
    const options = selectEl.querySelectorAll("option");

    options.forEach((option) => {
      if (isTriggered && !triggeredAllowedValues.includes(option.value)) {
        option.hidden = true;
        option.disabled = true;
        if (option.selected) {
          selectEl.value = "unfiltered";
        }
      } else {
        option.hidden = false;
        option.disabled = false;
      }
    });
  }

  toggleGroupVisibility(groups, visible) {
    groups.forEach((group) => {
      if (!group) return;

      group.hidden = !visible;
      if (visible) {
        group.classList.remove("d-none");
        group.style.removeProperty("display");
      } else {
        group.classList.add("d-none");
        group.style.display = "none";
      }
    });
  }

  togglePrimerFields(selector, visible) {
    const elements = this.element.querySelectorAll(selector);
    elements.forEach((el) => {
      this.togglePrimerField(el, !visible);
    });
  }

  togglePrimerField(input, hidden) {
    let current = input;
    let traversalCount = 0;

    while (current && current !== document.body && traversalCount < 4) {
      if (current.tagName) {
        const tag = current.tagName.toLowerCase();
        if (tag === "primer-text-field" || tag === "primer-select" || tag === "primer-checkbox" ||
            (tag === "div" && current.classList.contains("FormControl-checkbox-wrap"))) {
          current.hidden = hidden;

          if (hidden) {
            const inputs = current.querySelectorAll("input, select, textarea");
            inputs.forEach((inputEl) => {
              if (inputEl.type === "checkbox" || inputEl.type === "radio") {
                inputEl.checked = inputEl.defaultChecked;
              } else if (inputEl.tagName.toLowerCase() === "select") {
                if (inputEl.dataset.defaultValue !== undefined) {
                  inputEl.value = inputEl.dataset.defaultValue;
                }
              } else {
                inputEl.value = inputEl.defaultValue;
              }
            });
          }
          break;
        }
      }
      current = current.parentElement;
      traversalCount += 1;
    }
  }
}
